import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Target,
  Award,
  Calendar,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import { VictoryChart, VictoryBar, VictoryAxis, VictoryTheme, VictoryPie, VictoryLabel, VictoryLegend, VictoryStack, VictoryLine } from 'victory';

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardAnalyticsPDI = () => {
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);
  const [filtros, setFiltros] = useState({
    area: 'todas',
    periodo: '30dias',
    cargo: 'todos'
  });

  useEffect(() => {
    loadDashboard();
  }, [filtros]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API}/api/pdi-completo/relatorios/executivo`, {
        params: {
          area: filtros.area !== 'todas' ? filtros.area : undefined
        }
      });
      
      setDados(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const renderKPIs = () => {
    if (!dados) return null;

    const kpis = dados.kpis || {};

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de PDIs</p>
                <p className="text-3xl font-bold text-blue-700">{kpis.total_pdis || 0}</p>
              </div>
              <Users className="h-10 w-10 text-blue-600" />
            </div>
            <div className="mt-2 text-xs text-gray-600">
              {kpis.pdis_ativos || 0} ativos
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Taxa de Conclusão</p>
                <p className="text-3xl font-bold text-green-700">{kpis.taxa_conclusao_pdis?.toFixed(1) || 0}%</p>
              </div>
              <Target className="h-10 w-10 text-green-600" />
            </div>
            <div className="mt-2 text-xs text-gray-600">
              {kpis.pdis_concluidos || 0} concluídos
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Progresso Médio</p>
                <p className="text-3xl font-bold text-purple-700">{kpis.progresso_medio?.toFixed(1) || 0}%</p>
              </div>
              <TrendingUp className="h-10 w-10 text-purple-600" />
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Evolução geral
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ações Concluídas</p>
                <p className="text-3xl font-bold text-orange-700">{kpis.taxa_conclusao_acoes?.toFixed(1) || 0}%</p>
              </div>
              <Award className="h-10 w-10 text-orange-600" />
            </div>
            <div className="mt-2 text-xs text-gray-600">
              {kpis.acoes_concluidas || 0} de {kpis.total_acoes || 0}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderGraficoBarras = () => {
    if (!dados?.competencias_mais_desenvolvidas || dados.competencias_mais_desenvolvidas.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          Sem dados para exibir
        </div>
      );
    }

    const dataGrafico = dados.competencias_mais_desenvolvidas.slice(0, 10).map(item => ({
      competencia: item.competencia.length > 20 ? item.competencia.substring(0, 20) + '...' : item.competencia,
      quantidade: item.quantidade
    }));

    return (
      <div style={{ width: '100%', height: 400 }}>
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={20}
          padding={{ top: 20, bottom: 80, left: 60, right: 20 }}
        >
          <VictoryAxis
            tickLabelComponent={<VictoryLabel angle={-45} textAnchor="end" />}
            style={{ tickLabels: { fontSize: 10, padding: 5 } }}
          />
          <VictoryAxis
            dependentAxis
            label="Quantidade de PDIs"
            style={{
              axisLabel: { fontSize: 12, padding: 40 },
              tickLabels: { fontSize: 10 }
            }}
          />
          <VictoryBar
            data={dataGrafico}
            x="competencia"
            y="quantidade"
            style={{
              data: { 
                fill: ({ datum }) => {
                  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                  return colors[datum._x % colors.length];
                }
              }
            }}
            labels={({ datum }) => datum.quantidade}
            labelComponent={<VictoryLabel dy={-5} />}
          />
        </VictoryChart>
      </div>
    );
  };

  const renderGraficoPizza = () => {
    if (!dados?.kpis) return null;

    const kpis = dados.kpis;
    const dataPizza = [
      { x: 'Concluídos', y: kpis.pdis_concluidos || 0 },
      { x: 'Ativos', y: kpis.pdis_ativos || 0 },
      { x: 'Pendentes', y: Math.max(0, (kpis.total_pdis || 0) - (kpis.pdis_concluidos || 0) - (kpis.pdis_ativos || 0)) }
    ].filter(item => item.y > 0);

    if (dataPizza.length === 0) {
      return <div className="text-center py-12 text-gray-500">Sem dados</div>;
    }

    return (
      <div style={{ width: '100%', height: 350 }}>
        <VictoryPie
          data={dataPizza}
          colorScale={['#10b981', '#3b82f6', '#f59e0b']}
          labels={({ datum }) => `${datum.x}\n${datum.y}`}
          style={{
            labels: { fontSize: 12, fill: 'white' }
          }}
          innerRadius={70}
          labelRadius={100}
        />
      </div>
    );
  };

  const renderHeatmap = () => {
    // Usar dados reais do backend
    const heatmapData = dados?.heatmap_areas || [];
    
    if (heatmapData.length === 0) {
      return (
        <div className="text-center py-12">
          <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Sem dados de desenvolvimento por área ainda</p>
        </div>
      );
    }
    
    const niveis = ['baixo', 'medio', 'alto'];
    const niveisLabels = { 'baixo': 'Baixo', 'medio': 'Médio', 'alto': 'Alto' };
    
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="p-3 text-left text-sm font-semibold">Área</th>
              {niveis.map(nivel => (
                <th key={nivel} className="p-3 text-center text-sm font-semibold">{niveisLabels[nivel]}</th>
              ))}
              <th className="p-3 text-center text-sm font-semibold">Média</th>
            </tr>
          </thead>
          <tbody>
            {heatmapData.map((item, idx) => {
              return (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-semibold">{item.area}</td>
                  {niveis.map(nivel => {
                    const valor = item[nivel];
                    const intensidade = valor > 5 ? 'bg-green-500' : valor > 2 ? 'bg-yellow-500' : valor > 0 ? 'bg-orange-500' : 'bg-gray-300';
                    
                    return (
                      <td key={nivel} className="p-3">
                        <div className={`${intensidade} text-white rounded-lg p-2 text-center font-bold`}>
                          {valor}
                        </div>
                      </td>
                    );
                  })}
                  <td className="p-3 text-center font-bold text-lg">{item.media}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRanking = () => {
    // Usar dados reais do backend
    const ranking = dados?.ranking_evolucao || [];
    
    if (ranking.length === 0) {
      return (
        <div className="text-center py-12">
          <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum colaborador com PDI avaliado ainda</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {ranking.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div className="flex items-center space-x-3">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                idx === 0 ? 'bg-yellow-500 text-white' :
                idx === 1 ? 'bg-gray-400 text-white' :
                idx === 2 ? 'bg-orange-600 text-white' :
                'bg-gray-300 text-gray-700'
              }`}>
                {idx + 1}
              </div>
              <div>
                <div className="font-semibold">{item.nome}</div>
                <div className="text-xs text-gray-600">{item.pdis} PDI(s) concluído(s)</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-600">{item.evolucao?.toFixed(1) || 0}%</div>
              <div className="text-xs text-gray-600">evolução</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="font-semibold">Filtros:</span>
            </div>

            <Select value={filtros.area} onValueChange={(value) => setFiltros({...filtros, area: value})}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as Áreas</SelectItem>
                <SelectItem value="TI">TI</SelectItem>
                <SelectItem value="RH">RH</SelectItem>
                <SelectItem value="Comercial">Comercial</SelectItem>
                <SelectItem value="Financeiro">Financeiro</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filtros.periodo} onValueChange={(value) => setFiltros({...filtros, periodo: value})}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7dias">Últimos 7 dias</SelectItem>
                <SelectItem value="30dias">Últimos 30 dias</SelectItem>
                <SelectItem value="90dias">Últimos 90 dias</SelectItem>
                <SelectItem value="ano">Este ano</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={loadDashboard} disabled={loading} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>

            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {renderKPIs()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Barras */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              <span>Competências Mais Desenvolvidas</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderGraficoBarras()}
          </CardContent>
        </Card>

        {/* Gráfico de Pizza */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-green-600" />
              <span>Distribuição de Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderGraficoPizza()}
          </CardContent>
        </Card>
      </div>

      {/* Heatmap */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            <span>Heatmap de Desenvolvimento por Área</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderHeatmap()}
        </CardContent>
      </Card>

      {/* Ranking */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Award className="h-5 w-5 text-yellow-600" />
            <span>Ranking de Evolução - Top 5</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderRanking()}
        </CardContent>
      </Card>

      {/* Maturidade */}
      {dados && (
        <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2">Maturidade do Capital Humano</h3>
                <p className="text-3xl font-bold text-purple-700">{dados.maturidade_capital_humano || 'N/A'}</p>
              </div>
              <TrendingUp className="h-16 w-16 text-purple-600" />
            </div>
            {dados.recomendacoes && dados.recomendacoes.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-semibold mb-2">Recomendações:</h4>
                <ul className="space-y-1">
                  {dados.recomendacoes.map((rec, idx) => (
                    <li key={idx} className="text-sm text-gray-700">• {rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardAnalyticsPDI;
